import React, { useState, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { SidebarContext } from "context/SidebarContext";
import WhatsAppServices from "services/WhatsAppServices";
import DrawerButton from "components/form/DrawerButton";
import LabelArea from "components/form/LabelArea";
import Error from "components/form/Error";
import { DataType } from "ajv/dist/compile/validate/dataType";
import TemplatePreview from "components/whatsapp/TemplatePreview";
import { notifyError, notifySuccess } from "utils/toast";

const MessageDrawer = () => {
  const { setIsUpdate, toggleDrawer } = useContext(SidebarContext);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [values, setValues] = useState({});

  useEffect(() => {
    const fetchApprovedTemplates = async () => {
      try {
        const res = await WhatsAppServices.getApprovedTemplates();
        // console.log("approved template---", res);

        if (res && Array.isArray(res.data)) {
          setTemplates(res.data);
          // console.log("template from db---", res.data);
        } else if (Array.isArray(res)) {
          // in case API directly returns an array
          setTemplates(res);
        } else {
          console.error("Unexpected template response:", res);
          setTemplates([]);
        }
      } catch (error) {
        console.error("Failed to fetch approved templates:", error);
        setTemplates([]);
      }
    };

    fetchApprovedTemplates();
  }, []);

  const handleTemplateChange = (templateId) => {
    // console.log("Template dropdown changed:", templateId);
    const template = templates.find((t) => String(t.id) === String(templateId));
    // console.log("Selected Template:", template);
    setSelectedTemplate(template);
  };

  const onSubmit = async (data) => {
    if (!selectedTemplate) return;

 const payload = {
  messaging_product: "whatsapp",
  message_type: data.message_type,
  to: data.to,
  template_id: selectedTemplate.id,
  template_name: selectedTemplate.name,
  category: data.category,
  type: "template",
  content: {
    name: selectedTemplate.name,
    language: selectedTemplate.language, 
    components: selectedTemplate?.components?.map((component) => {
      const obj = { type: component.type };

      // Include text if HEADER or BODY
      if (component.type === "BODY" || component.type === "HEADER") {
        obj.text = component.text;

        // Optional: handle template variables {{1}}, {{2}}, etc.
        if (component.text.includes("{{1}}") && data.variables) {
          obj.parameters = data.variables.map((v) => ({ type: "text", text: v }));
        }
      }

      // Add FOOTER, BUTTONS, MEDIA handling if needed

      return obj;
    }) || [],
  },
};

    selectedTemplate?.components?.forEach((c) => {
      if (c.type === "BODY" || c.type === "HEADER") {
        const vars = c.text.match(/{{\d+}}/g) || [];
        if (vars.length > 0) {
          payload.content.components.push({
            type: c.type.toLowerCase(),
            parameters: vars.map((v, i) => ({
              type: "text",
              text: values[i + 1],
            })),
          });
        }
      }
    });

    try {
  await WhatsAppServices.sendMessage(payload);
  notifySuccess("Message sent successfully");
  toggleDrawer();
  setIsUpdate(true);
} catch (err) {
  console.error("Message send failed:", err);
  notifyError(err.response?.data?.message || "Failed to send message");
}

  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6">
      {/* Message Type */}
      <LabelArea required label="Message Type" className="mt-4" />
      <select
        {...register("message_type", { required: "Message type is required" })}
        className="w-full px-3 py-2 border rounded"
      >
        <option value="">Select Type</option>
        <option value="single">Single</option>
        <option value="bulk">Bulk</option>
      </select>
      <Error errorName={errors.message_type} />

      {/* Category Type */}
      <LabelArea required label="Category" className="mt-4" />
      <select
        {...register("category", { required: "Category is required" })}
        className="w-full px-3 py-2 border rounded"
      >
        <option value="">Select Category</option>
        <option value="driver">Driver</option>
        <option value="agent">Agent</option>
        <option value="vendor">Vendor</option>
      </select>
      <Error errorName={errors.category} />

      {/* Recipient Phone Number */}
      <LabelArea required label="Recipient Phone Number" />
      <input
        {...register("to", {
          required: "Recipient is required",
          validate: (value) => {
            if (!/^\d*$/.test(value)) return "Only numbers 0-9 are allowed";
            if (value.length !== 10)
              return "Phone number must be exactly 10 digits";
            return true;
          },
        })}
        inputMode="numeric"
        maxLength={10}
        onInput={(e) => {
          e.target.value = e.target.value.replace(/\D/g, "");
        }}
        className={`w-full px-3 py-2 border rounded ${
          errors.to ? "border-red-500" : "border-gray-300"
        }`}
        placeholder="e.g. 9876543210"
      />
      <Error errorName={errors.to} />

      {/* Select Template */}
      <LabelArea required label="Select Template" className="mt-4" />
      <select
        className="w-full px-3 py-2 border rounded"
        {...register("template", {
          required: "Template is required",
          onChange: (e) => handleTemplateChange(e.target.value),
        })}
      >
        <option value="">Select Template</option>
        {templates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} ({t.language})
          </option>
        ))}
      </select>

      <Error errorName={errors.template} />

      {/* Template Components */}
      {selectedTemplate && (
        <TemplatePreview
          key={selectedTemplate.id}
          selectedTemplate={selectedTemplate}
          values={values}
          setValues={setValues}
        />
      )}

      <div className="mt-4">
        <DrawerButton title="Send" isSubmitting={false} />
      </div>
    </form>
  );
};

export default MessageDrawer;
